import type { ReactElement, ReactNode } from "react";
import { Tooltip as RadixTooltip } from "radix-ui";
import { motion } from "motion/react";
import { spring } from "./motion";

/** Shares the open delay between tooltips, so moving from one trigger to the next shows the next at once. */
export function TooltipProvider({ children }: { children: ReactNode }) {
  return (
    <RadixTooltip.Provider delayDuration={350} skipDelayDuration={250}>
      {children}
    </RadixTooltip.Provider>
  );
}

export type TooltipProps = {
  label: ReactNode;
  /** A shortcut shown after the label, e.g. "⌘B". */
  keys?: string;
  side?: "top" | "right" | "bottom" | "left";
  /** Keeps the trigger mounted the same way while showing nothing, for a label that is already on screen. */
  hidden?: boolean;
  children: ReactElement;
};

/** A small dark label on hover and keyboard focus. Needs a `TooltipProvider` above it. */
export function Tooltip({ label, keys, side = "right", hidden, children }: TooltipProps) {
  return (
    <RadixTooltip.Root>
      <RadixTooltip.Trigger asChild>{children}</RadixTooltip.Trigger>
      {hidden ? null : (
        <RadixTooltip.Portal>
          <RadixTooltip.Content side={side} sideOffset={10} collisionPadding={8} className="z-[60] outline-none">
            <motion.div
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={spring}
              style={{ transformOrigin: "var(--radix-tooltip-content-transform-origin)" }}
              className="flex items-center gap-2 whitespace-nowrap rounded-sm bg-ink px-2 py-1 text-[12.5px] font-semibold text-surface shadow-raised"
            >
              {label}
              {keys ? <span className="font-medium text-surface/55">{keys}</span> : null}
            </motion.div>
          </RadixTooltip.Content>
        </RadixTooltip.Portal>
      )}
    </RadixTooltip.Root>
  );
}
